import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useFilterButtons } from '@/common/entityFramework/entityPerspective/hooks/useFilterButtons';
import { RowIcon } from '@/common/entityFramework/entityPerspective/support/RowIcon';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fad, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useTheme } from '@mui/material';
import { useCallback, useRef } from 'react';

export const DemReservePerspective = () => {

  const { palette } = useTheme();

  const entityPerspectiveRef = useRef();

  const views = [
    {
      name: 'Balances',
      dims: ['PAID_PERIOD', 'CHANNEL', 'PROD', 'EARNED_PERIOD'],
      kpis: ['START_BAL', 'ACCR_AMT', 'ACCR_ADJ', 'TRUE_UP', 'TOTAL_ACCR', 'PYMT_AMT', 'PYMT_ADJ', 'TOTAL_PYMT', 'END_BAL'],
      numFrozenDims: 1,
      dimToPeriodicities: {
        PAID_PERIOD: ['YEAR', 'QTR', 'MON'],
        EARNED_PERIOD: ['YEAR', 'QTR']
      }
    },

    {
      name: 'Aging',
      dims: ['EARNED_PERIOD', 'PAID_PERIOD', 'CHANNEL', 'PROD'],
      kpis: ['START_BAL', 'ACCR_AMT', 'ACCR_ADJ', 'TRUE_UP', 'TOTAL_ACCR', 'PYMT_AMT', 'PYMT_ADJ', 'TOTAL_PYMT', 'END_BAL'],
      numFrozenDims: 1,
      dimToPeriodicities: {
        PAID_PERIOD: ['YEAR', 'QTR', 'MON'],
        EARNED_PERIOD: ['YEAR', 'QTR']
      }
    }
  ]

  const { filterButtonStages, filterButtons, onViewReset } = useFilterButtons([
    // {
    //   key: 'negEndBal',
    //   icon: fas.faExclamationTriangle,
    //   colors: [null, 'red', 'green'],
    //   toolTips: ['Positive and negative end balance', 'Negative end balance', 'Positive end balance']
    // },
    {
      key: 'released',
      icon: fad.faPenToSquare,
      colors: [null, palette.success.dark, 'grey'],
      toolTips: ['Released and not released', 'Released', 'Not released'],
    }
  ])

  const augButtons = (buttons) => {
    buttons.push(...filterButtons, <TitleButtonDivider key="filterButtonDivider" />);
  }

  const augReqParam = useCallback((reqParam) => {
    // Negative end balance
    const negEndBalButtonState = filterButtonStages.negEndBal;
    if (negEndBalButtonState > 0) {
      reqParam.expr = ExprUtils.and(
        reqParam.expr,
        negEndBalButtonState === 1 ? ExprUtils.whereParams('negBal', 1) : ExprUtils.isNot('negBal', 1)
      )
    }

    // Released
    const releasedButtonState = filterButtonStages.released;
    if (releasedButtonState > 0) {
      reqParam.expr = ExprUtils.and(
        reqParam.expr,
        releasedButtonState === 1 ? ExprUtils.whereParams('released', 1) : ExprUtils.isNot('released', 1)
      )
    }
  }, [filterButtonStages])

  const rowIconsFn = (data) => {

    let rowIcons = [];

    if (data.closed) {
      rowIcons.push(<RowIcon key="closed" icon={fas.faCheckCircle} color={palette.success.dark} toolTip="Period is closed" />);
    }

    // if (data.negBal === 1) {
    //   rowIcons.push(<RowIcon key="negEndBal" icon={fas.faExclamationTriangle} color={palette.error.main} toolTip="End balance is negative" />);
    // } else if (data.negBal > 0) {
    //   rowIcons.push(<RowIcon key="containsNegEndBal" icon={fas.faExclamationTriangle} color={palette.error.light} toolTip="Contains an end balance that is negative" />);
    // }

    if (data.released === 1) {
      rowIcons.push(<RowIcon key="released" icon={fad.faPenToSquare} color={palette.success.dark} toolTip="Released" />);
    } else if (data.released > 0) {
      rowIcons.push(<RowIcon key="notReleased" icon={fad.faPenToSquare} color={palette.success.main} toolTip="Contains released item(s)" />);
    }

    return rowIcons;
  }

  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);
    
    const navigationItems = [
      {
        name: 'Navigate to Payment Adjustments',
        icon: fas.faSlidersH,
        action: () => entityPerspectiveRef.current.navigateToPerspectiveAdj(params, 'PYMT_ADJ')
      },
      {
        name: 'Navigate to RCM Payments',
        icon: fas.faListUl,
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'PYMT_AMT', 'RCM Payments')
      },
    ];

    const adjustmentItems = [
      {
        name: 'Adjust Accrual Amount',
        icon: fas.faPencil,
        action: () => entityPerspectiveRef.current.overwrite(params, 'ACCR_ADJ')
      }
    ];

    contextMenus.push(
      {
        name: 'Transactions',
        icon: fas.faListUl,
        subMenu: navigationItems,
      },
      {
        name: 'Adjust',
        icon: fas.faPencil,
        subMenu: adjustmentItems,
      }, {
        name: 'Release',
        action: () => entityPerspectiveRef.current.openActionDialog(params, 'Release', 'RESERVE_RELEASE_ACTION_PLUGIN'),
        icon: fad.faPenToSquare
      }
    );
    

    return contextMenus;
  }

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      views={views}
      augButtons={augButtons}
      augReqParam={augReqParam}
      rowIconsFn={rowIconsFn}
      onViewReset={onViewReset}
      gridOptions={{
        getContextMenuItems
      }} />
  )

}